import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import flatten from 'ramda/es/flatten'
import * as Sentry from '@sentry/browser'
import { persistPiniaSlice, loadPiniaSlice } from '../persist'
import { clearTestSession, scheduleTestSessionPatch } from '../testSession'

const emptyItem = {
    mainCategoryID: undefined,
    subCategories: [{ subCategoryID: undefined, questions: [] }],
}
const emptySubCat = emptyItem.subCategories[0]

const existsMainCat = (id, state) =>
    state.allTestQuestions.find((mc) => mc.mainCategoryID === id) !== undefined
const hasQuestions = (state) => state.allTestQuestions.length > 0

const hasValidCurrentMainCat = (state) => {
    const mainCat = state.allTestQuestions[state.currentTestMainCategoryIndex]
    return !!mainCat && Array.isArray(mainCat.subCategories)
}

const describeTestTemplateNavState = (state) => ({
    templateID: state.templateID,
    currentTestMainCategoryIndex: state.currentTestMainCategoryIndex,
    currentTestSubCategoryIndex: state.currentTestSubCategoryIndex,
    currentTestQuestionIndex: state.currentTestQuestionIndex,
    overallIndexOfCurrentSubCategory: state.overallIndexOfCurrentSubCategory,
    allTestQuestionsLength: state.allTestQuestions.length,
    mainCategoryIDs: state.allTestQuestions.map((mc) => mc?.mainCategoryID),
    subCategoryCounts: state.allTestQuestions.map((mc) =>
        Array.isArray(mc?.subCategories) ? mc.subCategories.length : null
    ),
})

/**
 * ClickUp: https://app.clickup.com/t/86cb3jcn0
 * Search Sentry for: CU-86cb3jcn0 or ETRAINER-APP-2H1
 */
const reportInvalidTestTemplateState = (state, where) => {
    const extra = {
        clickUpTaskId: '86cb3jcn0',
        relatedSentryIssue: 'ETRAINER-APP-2H1',
        where,
        ...describeTestTemplateNavState(state),
    }
    console.error('[testtemplate] invalid navigation state', extra)
    Sentry.captureMessage(
        `[CU-86cb3jcn0][ETRAINER-APP-2H1] Invalid testtemplate navigation state (${where})`,
        {
            level: 'error',
            tags: {
                clickup_task: '86cb3jcn0',
                related_issue: 'ETRAINER-APP-2H1',
                area: 'testtemplate',
            },
            extra,
        }
    )
}

const getCurrentMainCat = (state) =>
    hasQuestions(state) && hasValidCurrentMainCat(state)
        ? state.allTestQuestions[state.currentTestMainCategoryIndex]
        : emptyItem
const getCurrentSubCat = (state) =>
    getCurrentMainCat(state).subCategories[state.currentTestSubCategoryIndex] ??
    emptySubCat
const getCurrentQuestion = (state) =>
    getCurrentSubCat(state).questions[state.currentTestQuestionIndex]
const getPreviousQuestion = (state) =>
    getCurrentSubCat(state).questions[
        state.currentTestQuestionIndex <= 0
            ? 0
            : state.currentTestQuestionIndex - 1
    ]
const getNextQuestion = (state) =>
    state.currentTestQuestionIndex ===
    getCurrentSubCat(state).questions.length - 1
        ? null
        : getCurrentSubCat(state).questions[state.currentTestQuestionIndex + 1]
const isLastMainCategory = (state) =>
    state.currentTestMainCategoryIndex >= state.allTestQuestions.length - 1
const isLastSubCategoryInCurrentMainCategory = (state) =>
    state.currentTestSubCategoryIndex >=
    getCurrentMainCat(state).subCategories.length - 1
const isLastCategory = (state) =>
    isLastMainCategory(state) && isLastSubCategoryInCurrentMainCategory(state)

export const useTestTemplateStore = defineStore('testTemplate', () => {
    const templateID = ref(undefined)
    const templateProfessionID = ref(undefined)
    const templateDurationType = ref(undefined)
    const generatedTestID = ref(undefined)

    const overallIndexOfCurrentSubCategory = ref(0)
    const currentTestMainCategoryIndex = ref(0)
    const currentTestSubCategoryIndex = ref(0)
    const currentTestQuestionIndex = ref(0)
    const allTestQuestions = ref([])

    const persist = ({ session = 'debounce' } = {}) => {
        persistPiniaSlice('testTemplateStore', {
            templateID: templateID.value,
            templateProfessionID: templateProfessionID.value,
            templateDurationType: templateDurationType.value,
            generatedTestID: generatedTestID.value,
            overallIndexOfCurrentSubCategory: overallIndexOfCurrentSubCategory.value,
            currentTestMainCategoryIndex: currentTestMainCategoryIndex.value,
            currentTestSubCategoryIndex: currentTestSubCategoryIndex.value,
            currentTestQuestionIndex: currentTestQuestionIndex.value,
        })
        if (session === false || allTestQuestions.value.length === 0) return
        scheduleTestSessionPatch(
            { allTestQuestions: allTestQuestions.value },
            { immediate: session === 'immediate' }
        )
    }

    const hydrateAllTestQuestions = (questions) => {
        if (Array.isArray(questions)) allTestQuestions.value = questions
    }

    const snap = () => ({
        templateID: templateID.value,
        templateProfessionID: templateProfessionID.value,
        templateDurationType: templateDurationType.value,
        generatedTestID: generatedTestID.value,
        overallIndexOfCurrentSubCategory: overallIndexOfCurrentSubCategory.value,
        currentTestMainCategoryIndex: currentTestMainCategoryIndex.value,
        currentTestSubCategoryIndex: currentTestSubCategoryIndex.value,
        currentTestQuestionIndex: currentTestQuestionIndex.value,
        allTestQuestions: allTestQuestions.value,
    })

    const currentTestMainCategoryId = computed(
        () => getCurrentMainCat(snap()).mainCategoryID
    )
    const currentTestSubCategoryId = computed(
        () => getCurrentSubCat(snap()).subCategoryID
    )
    const currentTestQuestion = computed(() => getCurrentQuestion(snap()))
    const previousTestQuestion = computed(() => getPreviousQuestion(snap()))
    const currentTestQuestions = computed(() => getCurrentSubCat(snap()).questions)
    const currentTestQuestionsCount = computed(
        () => getCurrentSubCat(snap()).questions.length
    )

    const remainingTestQuestionsOfCurrentSubCategory = computed(() =>
        getCurrentSubCat(snap()).questions.slice(
            currentTestQuestionIndex.value + 1
        )
    )

    const allRemainingQuestions = computed(() => {
        const state = snap()
        return flatten(
            state.allTestQuestions.map((mc, index) => {
                if (state.currentTestMainCategoryIndex > index) return []
                if (state.currentTestMainCategoryIndex < index)
                    return mc.subCategories.map((sc) => sc.questions)

                return mc.subCategories.map((sc, scIndex) => {
                    if (state.currentTestSubCategoryIndex > scIndex) return []
                    if (state.currentTestSubCategoryIndex < scIndex)
                        return sc.questions

                    return sc.questions.slice(
                        state.currentTestQuestionIndex + 1
                    )
                })
            })
        )
    })

    const allTestQuestionsAlone = computed(() =>
        flatten(
            allTestQuestions.value.map((mc) =>
                mc.subCategories.map((sc) => sc.questions)
            )
        )
    )

    const subcategoriesOfMainCategory = (id) => {
        const state = snap()
        if (!existsMainCat(id, state)) return []
        return state.allTestQuestions.find((mc) => mc.mainCategoryID === id)
            .subCategories
    }

    const isLastSubCategory = computed(() => isLastCategory(snap()))
    const isLastQuestion = computed(() => {
        const state = snap()
        if (!isLastMainCategory(state)) return false
        if (!isLastSubCategoryInCurrentMainCategory(state)) return false
        return (
            state.currentTestQuestionIndex >=
            getCurrentSubCat(state).questions.length - 1
        )
    })
    const isFirstQuestionOverall = computed(
        () =>
            currentTestQuestionIndex.value === 0 &&
            currentTestSubCategoryIndex.value === 0 &&
            currentTestMainCategoryIndex.value === 0
    )
    const isLastQuestionInSubCategory = computed(
        () =>
            getCurrentSubCat(snap()).questions.length - 1 <=
            currentTestQuestionIndex.value
    )
    const isFirstQuestionInSubCategory = computed(
        () => currentTestQuestionIndex.value === 0
    )
    const isLastQuestionInBlock = computed(() => {
        const state = snap()
        return (
            isLastQuestionInSubCategory.value ||
            getCurrentQuestion(state).block != getNextQuestion(state).block
        )
    })
    const isFirstQuestionInBlock = computed(() => {
        const state = snap()
        return (
            state.currentTestQuestionIndex === 0 ||
            getCurrentQuestion(state).block != getPreviousQuestion(state).block
        )
    })
    const countOfAllSubCategories = computed(() =>
        allTestQuestions.value.reduce(
            (acc, mc) => acc + mc.subCategories.length,
            0
        )
    )

    const resetTestTemplate = () => {
        templateID.value = undefined
        templateProfessionID.value = undefined
        templateDurationType.value = undefined
        generatedTestID.value = undefined
        allTestQuestions.value = []
        overallIndexOfCurrentSubCategory.value = 0
        currentTestMainCategoryIndex.value = 0
        currentTestSubCategoryIndex.value = 0
        currentTestQuestionIndex.value = 0
        persist({ session: false })
        clearTestSession()
    }

    const setTestTemplateID = (id) => {
        templateID.value = id
        persist()
    }
    const setTestProfessionID = (id) => {
        templateProfessionID.value = id
        persist()
    }
    const setTestTemplateDurationType = (type) => {
        templateDurationType.value = type
        persist()
    }
    const setGeneratedTestID = (id) => {
        generatedTestID.value = id
        persist()
    }

    const addToAllTestQuestions = (questions) => {
        questions.forEach((q) => allTestQuestions.value.push(q))
        persist({ session: 'immediate' })
    }

    const goToNextTestQuestion = () => {
        const state = snap()
        if (hasQuestions(state) && !hasValidCurrentMainCat(state)) {
            reportInvalidTestTemplateState(state, 'go_to_next_test_question')
            return
        }

        if (isLastQuestion.value) return
        if (currentTestQuestionsCount.value > currentTestQuestionIndex.value + 1) {
            currentTestQuestionIndex.value++
            persist()
            return
        }

        currentTestQuestionIndex.value = 0
        if (
            getCurrentMainCat(snap()).subCategories.length >
            currentTestSubCategoryIndex.value + 1
        ) {
            currentTestSubCategoryIndex.value++
            overallIndexOfCurrentSubCategory.value++
            persist()
            return
        }

        if (isLastMainCategory(snap())) return

        currentTestSubCategoryIndex.value = 0
        overallIndexOfCurrentSubCategory.value++
        currentTestMainCategoryIndex.value++
        persist()
    }

    const goToPrevTestQuestion = () => {
        if (isFirstQuestionOverall.value) return

        const state = snap()
        if (hasQuestions(state) && !hasValidCurrentMainCat(state)) {
            reportInvalidTestTemplateState(state, 'go_to_prev_test_question')
            return
        }

        if (currentTestQuestionIndex.value > 0) {
            currentTestQuestionIndex.value--
        } else if (currentTestSubCategoryIndex.value > 0) {
            overallIndexOfCurrentSubCategory.value--
            currentTestSubCategoryIndex.value--
            currentTestQuestionIndex.value = currentTestQuestionsCount.value - 1
        } else if (currentTestMainCategoryIndex.value > 0) {
            currentTestMainCategoryIndex.value--
            overallIndexOfCurrentSubCategory.value--
            currentTestSubCategoryIndex.value =
                getCurrentMainCat(snap()).subCategories.length - 1
            currentTestQuestionIndex.value = currentTestQuestionsCount.value - 1
        } else {
            reportInvalidTestTemplateState(
                snap(),
                'go_to_prev_test_question:cannot_step_to_previous_main'
            )
            return
        }
        persist()
    }

    const goToNextTestSubCategory = () => {
        const state = snap()
        if (hasQuestions(state) && !hasValidCurrentMainCat(state)) {
            reportInvalidTestTemplateState(state, 'go_to_next_test_sub_category')
            return
        }

        if (isLastCategory(snap())) return

        if (isLastSubCategoryInCurrentMainCategory(snap())) {
            currentTestMainCategoryIndex.value++
            currentTestSubCategoryIndex.value = 0
        } else {
            currentTestSubCategoryIndex.value++
        }

        overallIndexOfCurrentSubCategory.value++
        currentTestQuestionIndex.value = 0
        persist()
    }

    const goToTestQuestionWithIndex = (idx) => {
        if (idx < 0 || idx >= currentTestQuestionsCount.value) return
        currentTestQuestionIndex.value = idx
        persist()
    }

    const markTestQuestion = () => {
        const q = getCurrentQuestion(snap())
        if (q) q.marked = true
        persist()
    }
    const unmarkTestQuestion = () => {
        const q = getCurrentQuestion(snap())
        if (q) q.marked = false
        persist()
    }

    const stored = loadPiniaSlice('testTemplateStore')
    if (stored) {
        if (stored.templateID !== undefined) templateID.value = stored.templateID
        if (stored.templateProfessionID !== undefined)
            templateProfessionID.value = stored.templateProfessionID
        if (stored.templateDurationType !== undefined)
            templateDurationType.value = stored.templateDurationType
        if (stored.generatedTestID !== undefined)
            generatedTestID.value = stored.generatedTestID
        if (typeof stored.overallIndexOfCurrentSubCategory === 'number')
            overallIndexOfCurrentSubCategory.value =
                stored.overallIndexOfCurrentSubCategory
        if (typeof stored.currentTestMainCategoryIndex === 'number')
            currentTestMainCategoryIndex.value = stored.currentTestMainCategoryIndex
        if (typeof stored.currentTestSubCategoryIndex === 'number')
            currentTestSubCategoryIndex.value = stored.currentTestSubCategoryIndex
        if (typeof stored.currentTestQuestionIndex === 'number')
            currentTestQuestionIndex.value = stored.currentTestQuestionIndex
        if (Array.isArray(stored.allTestQuestions) && stored.allTestQuestions.length > 0)
            allTestQuestions.value = stored.allTestQuestions
    }

    return {
        templateID,
        templateProfessionID,
        templateDurationType,
        generatedTestID,
        overallIndexOfCurrentSubCategory,
        currentTestMainCategoryIndex,
        currentTestSubCategoryIndex,
        currentTestQuestionIndex,
        allTestQuestions,
        hydrateAllTestQuestions,

        currentTestMainCategoryId,
        currentTestSubCategoryId,
        currentTestQuestion,
        previousTestQuestion,
        currentTestQuestions,
        currentTestQuestionsCount,
        remainingTestQuestionsOfCurrentSubCategory,
        allRemainingQuestions,
        allTestQuestionsAlone,
        subcategoriesOfMainCategory,
        isLastSubCategory,
        isLastQuestion,
        isFirstQuestionOverall,
        isLastQuestionInSubCategory,
        isFirstQuestionInSubCategory,
        isLastQuestionInBlock,
        isFirstQuestionInBlock,
        countOfAllSubCategories,

        resetTestTemplate,
        setTestTemplateID,
        setTestProfessionID,
        setTestTemplateDurationType,
        setGeneratedTestID,
        addToAllTestQuestions,
        goToNextTestQuestion,
        goToPrevTestQuestion,
        goToNextTestSubCategory,
        goToTestQuestionWithIndex,
        markTestQuestion,
        unmarkTestQuestion,
    }
})
